import dayjs from "dayjs";

import { ISearchPeopleFilter, IPersonItem, IServicePersons, TService, TServiceGroup, ICustomEditItem } from "../interfaces";
import { getRomanPersons } from "./roman";
import { getLenkaPersons } from "./lenka";
import { getMakuderoviPersons, PERSONS_KEYS } from "./makuderovi";
import { getPersonAgeStr, isLocalhost } from "../utils/utils";
import { HOLIDAYS } from "./holidays";
import { CROSS_SIGN, PERMS, ROMAN_PERMS } from "../const-files/const";
import { getMakuderovFluffies } from "./fluffies";

const allPersonsObj: {[key: string]: IPersonItem;} = {};
const allPersons: Array<IPersonItem> = [];
const allCustomPersonsMakuderovi: Array<ICustomEditItem> = [{
	title: "RM",
	data: {
		persons: [PERSONS_KEYS.ROM_MAK],
	},
}, {
	title: "LM",
	data: {
		persons: [PERSONS_KEYS.LEN_MAK],
	},
}, {
	title: "MM",
	data: {
		persons: [PERSONS_KEYS.MAR_MAK],
	},
}, {
	title: "TM",
	data: {
		persons: [PERSONS_KEYS.TOM_MAK],
	},
}, {
	title: "My",
	data: {
		persons: [PERSONS_KEYS.ROM_MAK, PERSONS_KEYS.LEN_MAK],
	},
}, {
	title: "Děti",
	data: {
		persons: [PERSONS_KEYS.MAR_MAK, PERSONS_KEYS.TOM_MAK],
	},
}, {
	title: "RM+D",
	hover: "Roman + děti",
	data: {
		persons: [PERSONS_KEYS.ROM_MAK, PERSONS_KEYS.MAR_MAK, PERSONS_KEYS.TOM_MAK],
	},
}, {
	title: "LM+D",
	hover: "Lenka + děti",
	data: {
		persons: [PERSONS_KEYS.LEN_MAK, PERSONS_KEYS.MAR_MAK, PERSONS_KEYS.TOM_MAK],
	},
}, {
	title: "ROD",
	hover: "Celá rodina",
	data: {
		persons: [PERSONS_KEYS.ROM_MAK, PERSONS_KEYS.LEN_MAK, PERSONS_KEYS.MAR_MAK, PERSONS_KEYS.TOM_MAK],
	},
}, {
	title: "MM+školka",
	data: {
		persons: [PERSONS_KEYS.MAR_MAK],
		title: "Marťa",
	},
}, {
	title: "TM+školka",
	data: {
		persons: [PERSONS_KEYS.TOM_MAK],
		title: "Tomča",
	},
}, {
	title: "Děti školka",
	data: {
		persons: [PERSONS_KEYS.MAR_MAK, PERSONS_KEYS.TOM_MAK],
		title: "Marťa a Tomča",
	},
}];
const allCustomPersonsRoman: Array<ICustomEditItem> = [{
	title: "RM",
	data: {
		persons: [PERSONS_KEYS.ROM_MAK],
	},
}];
const allCustomPersonsLenka: Array<ICustomEditItem> = [{
	title: "LM",
	data: {
		persons: [PERSONS_KEYS.LEN_MAK],
	},
}];

function getPersonDesc(personData: IPersonItem): IPersonItem["desc"] {
	const parts: Array<string> = [];

	if (personData.info) {
		parts.push(personData.info);
	}

	if (personData.nickName) {
		parts.push(personData.nickName);
	}

	if (personData.maidenItems) {
		const allMaidenItems = personData.maidenItems.slice(1);

		parts.push(allMaidenItems.map(item => item.surname).join(", "));
	} else if (personData.maidenName) {
		parts.push(personData.maidenName[0]);
	}

	return {
		public: parts.join(", "),
		admin: [
			...parts,
			personData.group,
		].join(", "),
	};
}

function addPersons(personsService: TService, servicePersons: Array<IServicePersons>) {
	const localhost = isLocalhost();

	for (const servicePerson of servicePersons) {
		for (const person of servicePerson.items) {
			if (person.key in allPersonsObj) {
				/* eslint-disable-next-line */
				console.log(`Duplicate key ${person.key}`);
			}

			person.ageStr = "";

			if ("deceased" in person) {
				person.ageStr = `${person.birthdate && person.deceased ? `${dayjs(person.deceased).diff(dayjs(person.birthdate), "years")} `: ""}${CROSS_SIGN}`;
			} else if (person.birthdate) {
				person.ageStr = getPersonAgeStr(person.birthdate);
			}

			const firstName = person.fullName.split(" ")[0];

			if (person.calName && person.calName in HOLIDAYS) {
				person.holiday = HOLIDAYS[person.calName];
			} else if (firstName in HOLIDAYS) {
				person.holiday = HOLIDAYS[firstName];
			} else if (localhost && !person.hidden && !person.isFluffy && !person.fullName.toLowerCase().includes("xxx")) {
				/* eslint-disable-next-line */
				console.log(`Zadny svatek pro ${firstName} - ${person.fullName}`);
			}

			const fullPerson: IPersonItem = {
				...person,
				photoBest: null,
				photoCount: 0,
				desc: {
					public: "",
					admin: "",
				},
				group: servicePerson.group,
				service: personsService,
			};

			// init hodnota
			fullPerson.desc = getPersonDesc(fullPerson);

			// priradime
			Object.assign(person, fullPerson);

			allPersonsObj[person.key] = fullPerson;
			allPersons.push(fullPerson);
		}
	}
}

export function getPersonData(personKey: string) {
	return personKey in allPersonsObj
		? allPersonsObj[personKey]
		: null;
}

export function getPersonsData(personKeys: Array<string>) {
	const output: Array<IPersonItem> = [];

	for (const personKey of personKeys) {
		if (personKey in allPersonsObj) {
			output.push(allPersonsObj[personKey]);
		}
	}

	return output;
}

function filterPersonsByGroups(groups: Array<TServiceGroup>) {
	return allPersons.filter(item => groups.includes(item.group));
}

export function getBirthdayCalendarPeople(filter: ISearchPeopleFilter) {
	if (filter.groups && (filter.perms === PERMS.ADMIN || filter.perms === ROMAN_PERMS.ADMIN)) {
		return filterPersonsByGroups(filter.groups);
	}

	switch (filter.service) {
		case "makuderovi":
			return filterPersonsByGroups(["makuderovi-nase-rodina", "makuderovi-rodina"]);

		case "roman":
			switch (filter.perms) {
				case ROMAN_PERMS.ADMIN:
					return allPersons;

				case ROMAN_PERMS.KAMOSI:
					return filterPersonsByGroups(["makuderovi-nase-rodina", "roman-kamosi", "roman-zakladka", "roman-stredni", "makuderovi-spolecni-kamosi"]);

				case ROMAN_PERMS.RODINA:
					return filterPersonsByGroups(["makuderovi-nase-rodina", "makuderovi-rodina"]);

				case ROMAN_PERMS.SEZNAM:
					return filterPersonsByGroups(["makuderovi-nase-rodina", "makuderovi-seznam"]);

				default:
			}
			break;

		default:
	}

	return [];
}

export function getGalleryPeople() {
	return allPersons.filter(people => people.photoCount > 0);
}

export function getAllFluffies() {
	return allPersons.filter(item => item.isFluffy);
}

export function getSearchPeople(filter: ISearchPeopleFilter) {
	if (filter.perms === PERMS.ADMIN || filter.perms === ROMAN_PERMS.ADMIN) {
		return allPersons;
	}

	switch (filter.service) {
		case "makuderovi":
			return filterPersonsByGroups(["makuderovi-nase-rodina", "makuderovi-rodina", "makuderovi-spolecni-kamosi", "makuderovi-skryti"]);

		case "roman":
			switch (filter.perms) {
				case ROMAN_PERMS.KAMOSI:
					return filterPersonsByGroups(["makuderovi-nase-rodina", "roman-kamosi", "roman-zakladka", "roman-stredni", "makuderovi-spolecni-kamosi"]);

				case ROMAN_PERMS.RODINA:
					return filterPersonsByGroups(["makuderovi-nase-rodina", "makuderovi-rodina", "makuderovi-spolecni-kamosi", "makuderovi-skryti"]);

				case ROMAN_PERMS.SEZNAM:
					return filterPersonsByGroups(["makuderovi-nase-rodina", "makuderovi-seznam"]);

				default:
			}
			break;

		default:
	}

	return [];
}

function initPersons() {
	// pridame osoby
	addPersons("makuderovi", getMakuderoviPersons());
	addPersons("makuderovi", getMakuderovFluffies());
	addPersons("roman", getRomanPersons());
	addPersons("lenka", getLenkaPersons());
}

export function getCustomButtons(service: TService): Array<ICustomEditItem> {
	switch (service) {
		case "makuderovi":
			return allCustomPersonsMakuderovi;

		case "roman":
			return allCustomPersonsRoman;

		case "lenka":
			return allCustomPersonsLenka;

		default:
			return [];
	}
}

// init
initPersons();

export interface IPersonsGroupItem {
	group: TServiceGroup;
	persons: Array<IPersonItem>;
}

// klic osoby JMENO_PRIJMENI -> PRIJMENI_JMENO - razeni podle prijmeni, pak jmena
function getGroupSortKey(personKey: string) {
	const [firstName, ...surname] = personKey.split("_");

	return [...surname, firstName].join("_");
}

/**
 * Osoby z hledani podle skupin - poradi skupin podle seznamu osob, osoby podle prijmeni a jmena.
 */
export function getPersonsGroups(filter: ISearchPeopleFilter, personFilter: (person: IPersonItem) => boolean) {
	const groups: Array<IPersonsGroupItem> = [];

	for (const person of getSearchPeople(filter).filter(personFilter)) {
		let groupItem = groups.find(item => item.group === person.group);

		if (!groupItem) {
			groupItem = {
				group: person.group,
				persons: [],
			};
			groups.push(groupItem);
		}

		groupItem.persons.push(person);
	}

	for (const groupItem of groups) {
		groupItem.persons.sort((aPerson, bPerson) => getGroupSortKey(aPerson.key).localeCompare(getGroupSortKey(bPerson.key)));
	}

	return groups;
}
